/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext } from 'react';

import config from 'js/app/config';

import { Button, PageNotification, Typography2 } from '@coursera/cds-core';

import { SGAPageContext } from 'bundles/grading/components/staff-graded-assignments/SGAPageContext';

import _t from 'i18n!nls/grading';

const styles = {
  container: () => css`
    z-index: 4;

    .cds-localNotification-dismissible {
      margin-top: var(--cds-spacing-200);
    }
  `,
  nonDismissibleNotification: () => css`
    margin: 0 auto;
    margin-top: var(--cds-spacing-200);
  `,
};

// handles all global notifications that will float on top of content panels
const NotificationsGlobal: React.FC = () => {
  const { submitterList, submissionData } = useContext(SGAPageContext);

  const { submitters } = submitterList.data || {};
  // there can be team sga with no submitters possible;
  const noSubmittersWarning = !submitterList.loading && submitterList.data && (submitters || []).length === 0;

  const noCurrentSubmitterWarning =
    !submitterList.loading &&
    !submissionData.loading &&
    submissionData.data &&
    !submissionData.data.submitter &&
    (submitters || []).length > 0;

  const error = submitterList.error || submissionData.error;
  if (
    !error && // general querying errors
    submissionData.data?.submitter &&
    !noSubmittersWarning
  ) {
    return null;
  }

  let errorText;
  let errorTitle;
  let errorDetails;
  if (error) {
    errorTitle = _t('Something went wrong');
    if (error.graphQLErrors?.[0]?.extensions?.errorType === 'PERMISSION_DENIED') {
      errorText = _t(`You don't have access to this action. Please contact support.`);
    } else if (error.graphQLErrors?.[0]?.extensions?.errorType === 'NOT_FOUND') {
      errorTitle = _t('No submission found');
      errorText = _t('We couldn’t find a submission matching this learner ID. Please contact support.');
    } else {
      errorText = _t('Something went wrong while loading data. Please try again.');
    }
    errorDetails = error.message;
  }

  return (
    <div css={styles.container} data-testid="grading-notifications-global-container">
      {noSubmittersWarning && (
        // this warning is for the case where there are no submitters at all for assignment
        <PageNotification severity="warning" title={_t('Warning')} css={styles.nonDismissibleNotification}>
          {_t(`No learners found for this assignment.`)}
        </PageNotification>
      )}
      {noCurrentSubmitterWarning && (
        // this warning is for the case where submitter in route does not exist in assignment but there are other submitters available
        <PageNotification
          severity="warning"
          title={_t('This learner does not exist in this assignment')}
          css={styles.nonDismissibleNotification}
        >
          {_t(`Please select another learner.`)}
        </PageNotification>
      )}
      {errorText && (
        <PageNotification
          inlineAction={
            <Button size="small" variant="ghost" onClick={() => window.location.reload()}>
              {_t('Reload page')}
            </Button>
          }
          severity="error"
          title={errorTitle}
        >
          <div>
            {errorText}

            {/* Displaying error details for faster troubleshoot during initial release; Will only render in development environment */}
            {errorDetails && config.environment === 'development' && (
              <Typography2 component="div" variant="bodySecondary">
                Details: {errorDetails}
              </Typography2>
            )}
          </div>
        </PageNotification>
      )}
    </div>
  );
};

export default NotificationsGlobal;
